import { TypedClientApiError } from "@gosaas/api-client";
import * as Sentry from "@sentry/react";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { RouterProvider } from "@tanstack/react-router";
import { createRoot, hydrateRoot } from "react-dom/client";

// css imports
import "@/styles/main.css";
import "@/styles/mdx.css";

import { scan } from "react-scan";
import { AuthProvider } from "@/components/providers/auth";
import reportWebVitals from "@/reportWebVitals";
import { createAppRouter } from "@/router";
import { TailwindSizeDisplay } from "@/utils/breakpoint";
import { clearRouterManagedHeadTags } from "@/utils/hydration-head";
import { setSentryTraceHeadersProvider } from "@/utils/sentry-trace-headers";
import { applyThemeToDocument, getStoredTheme } from "@/utils/theme";
import { env } from "./env";

function initSentry() {
	Sentry.init({
		dsn: env.VITE_SENTRY_DSN,
		sendDefaultPii: true,
		integrations: [
			Sentry.replayIntegration({
				maskAllText: false,
				blockAllMedia: true,
			}),
			Sentry.browserTracingIntegration(),
		],
		tracesSampleRate: 0.1,
		tracePropagationTargets: ["localhost", env.VITE_BACKEND_URL],
		replaysSessionSampleRate: 0.05,
		replaysOnErrorSampleRate: 0.5,
	});

	setSentryTraceHeadersProvider(() => {
		const traceData = Sentry.getTraceData?.();
		if (!traceData) {
			return undefined;
		}
		const headers: Record<string, string> = {};
		if (traceData["sentry-trace"]) {
			headers["sentry-trace"] = traceData["sentry-trace"];
		}
		if (traceData.baggage) {
			headers.baggage = traceData.baggage;
		}
		return Object.keys(headers).length > 0 ? headers : undefined;
	});
}

const router = createAppRouter();

// Create a client
const queryClient = new QueryClient({
	defaultOptions: {
		mutations: {
			onError: (error) => {
				if (!(error instanceof TypedClientApiError)) {
					Sentry.captureException(error);
				}
			},
		},
	},
});

// Wrap your router/app with QueryClientProvider
function App() {
	return (
		<QueryClientProvider client={queryClient}>
			<AuthProvider>
				<RouterProvider router={router} />
				<TailwindSizeDisplay />
			</AuthProvider>
		</QueryClientProvider>
	);
}

scan({
	enabled: import.meta.env.DEV,
});

const rootElement = document.getElementById("app");

function startApp() {
	if (!rootElement) {
		queueMicrotask(initSentry);
		return;
	}

	const tree = <App />;
	if (rootElement.firstChild) {
		let bootstrapError: unknown;
		void router
			.load()
			.catch((error: unknown) => {
				bootstrapError = error;
			})
			.finally(() => {
				// Prerender injects route head tags outside React's tree; HeadContent
				// hoists duplicates on hydration → React #418 on <html>.
				clearRouterManagedHeadTags();
				hydrateRoot(rootElement, tree);
				// Apply stored theme after hydration so html/body classes match the
				// prerender shell (theme-init.js before hydrate caused React #418).
				applyThemeToDocument(getStoredTheme());
				// Replay records DOM snapshots; must not run until after hydrateRoot.
				queueMicrotask(() => {
					initSentry();
					if (bootstrapError !== undefined) {
						Sentry.captureException(bootstrapError);
					}
				});
			});
		return;
	}

	createRoot(rootElement).render(tree);
	queueMicrotask(initSentry);
}

startApp();

// If you want to start measuring performance in your app, pass a function
// to log results (for example: reportWebVitals(console.log))
// or send to an analytics endpoint. Learn more: https://bit.ly/CRA-vitals
reportWebVitals((metric) => {
	Sentry.captureEvent({
		type: "transaction",
		event_id: metric.id,
		transaction: metric.name,
		measurements: {
			[metric.name.toLowerCase()]: {
				value: metric.value,
				unit: metric.name === "CLS" ? "" : "millisecond",
			},
		},
		tags: { web_vitals_rating: metric.rating },
	});
});
